import { requireFieldsInfo } from "../components/Form/context/formSlice";
import {
  getSanitizedFormData,
  updateDatagridStatus,
  updateFormConditions,
} from "../components/Form/services/form.service";
import { axiosUploadHelper } from "./api";
import splitApi from "./emptySplitApi";
import { isLocalhost } from "../utils/helpers";

const applicationsApi = splitApi.injectEndpoints({
  endpoints: (build) => ({
    applications: build.query({
      query: (props) => ({
        url: "/api/applicationForms",
        params: props,
      }),
      transformResponse(baseQueryReturnValue) {
        const { pages, total, limit, page } = baseQueryReturnValue;
        // eslint-disable-next-line no-param-reassign
        baseQueryReturnValue.pagination = { pages, total, limit, page };
        return baseQueryReturnValue;
      },
      providesTags: ["Applications"],
    }),
    applicationRecord: build.query({
      query: (applicationId) => `api/applicationForms/${applicationId}`,
      providesTags: ["Application"],
    }),
    createApplication: build.mutation({
      query: (body) => ({
        url: "/api/applicationForms",
        method: "POST",
        body: getSanitizedFormData(body, null, { ...requireFieldsInfo }),
      }),
      invalidatesTags: ["Application", "Applications"],
    }),
    cloneApplication: build.mutation({
      query: (body) => ({
        url: "/api/applicationForms/clone",
        method: "POST",
        body: getSanitizedFormData(body, null, { ...requireFieldsInfo }),
      }),
    }),

    getRejectedApplications: build.query({
      query: (props) => ({
        url: "/api/applicationForms",
        params: props,
        method: "GET",
      }),
      providesTags: ["RejectedApplications"],
    }),

    getApplication: build.mutation({
      query: ({ id }) => ({
        url: `/api/applicationForms/${id}`,
        method: "GET",
      }),
      transformResponse(baseQueryReturnValue, meta, arg = {}) {
        const { allowDataTransform, dgProps } = arg;
        if (allowDataTransform) {
          const { formProperties } = baseQueryReturnValue;
          updateDatagridStatus(formProperties, dgProps);
        }
        return baseQueryReturnValue;
      },
    }),
    getFormConfig: build.mutation({
      query: (params) => ({
        url: "api/config/formConfig",
        method: "GET",
        params,
      }),
      transformResponse: async (baseQueryReturnValue) => {
        if (isLocalhost && process.env.REACT_APP_TEST_APP_FORM) {
          try {
            const response = await fetch(process.env.REACT_APP_TEST_APP_FORM);
            const externalForm = await response.json();
            return updateFormConditions(externalForm);
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error(
              `API CALL FAILED: ${process.env.REACT_APP_TEST_APP_FORM}`,
              error
            );
            return baseQueryReturnValue;
          }
        }
        return baseQueryReturnValue;
      },
    }),
    listConfig: build.query({
      query: () => ({
        url: "/dist/config/applications.json",
      }),
      providesTags: ["Config"],
    }),
    updateApplicationForm: build.mutation({
      query: ({ id, body, originalFormProps }) => ({
        url: `/api/applicationForms/${id}`,
        method: "PUT",
        body: getSanitizedFormData(body, originalFormProps, {
          ...requireFieldsInfo,
        }),
      }),
      invalidatesTags: ["Applications"],
    }),
    deleteApplication: build.mutation({
      query: ({ id }) => ({
        url: `api/applicationForms/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Applications"],
    }),
    restoreApplication: build.mutation({
      query: ({ id }) => ({
        url: `/api/applicationForms/restore/${id}`,
        method: "PUT",
      }),
      invalidatesTags: ["Applications"],
    }),
    saveApplicationForm: build.mutation({
      query: ({ id, body, originalFormProps }) => ({
        url: `/api/applicationForms/${id}/efile`,
        method: "POST",
        body: getSanitizedFormData(body, originalFormProps, {
          ...requireFieldsInfo,
          applicationStatus: "Open",
        }),
      }),
      invalidatesTags: ["Application", "Applications"],
    }),
    getDocuments: build.query({
      query: (applicationId) => ({
        url: `/api/applicationForms/${applicationId}/documents`,
      }),
      providesTags: ["Documents"],
    }),
    uploadDocument: build.mutation({
      // to track upload progress
      queryFn: async ({ body, setUploadProgress }) =>
        axiosUploadHelper({
          url: "/api/documents/upload",
          method: "POST",
          body,
          setUploadProgress,
        }),
      invalidatesTags: ["Documents"],
    }),
    updateDocument: build.mutation({
      // to track upload progress
      queryFn: async ({ versionSeriesId, body, setUploadProgress }) =>
        axiosUploadHelper({
          url: `/api/documents/${versionSeriesId}/updateVersion`,
          method: "POST",
          body,
          setUploadProgress,
        }),
      invalidatesTags: ["Documents"],
    }),
    deleteDocument: build.mutation({
      query: ({ applicationId, versionSeriesId }) => ({
        url: `/api/applicationForms/${applicationId}/documents/${versionSeriesId}`,
        method: "DELETE",
      }),
      // invalidatesTags: [],
    }),
    tableConfig: build.query({
      query: (applicationType) => ({
        url: `/dist/config/${applicationType.toLowerCase()}-tableConfig.json`,
      }),
      providesTags: ["Config"],
    }),
    forfeitApplication: build.mutation({
      query: ({ id, body }) => ({
        url: `/api/applicationForms/${id}/forfeit`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Applications"],
    }),
    getPublicStatus: build.query({
      query: (applicationId) => ({
        url: `/api/applicationForms/${applicationId}/publicStatus`,
      }),
      providesTags: ["Applications"],
    }),
    feeMap: build.query({
      query: () => "/api/config/feeMap",
    }),
  }),

  overrideExisting: false,
});

export const {
  useApplicationsQuery,
  useTableConfigQuery,
  useCreateApplicationMutation,
  useCloneApplicationMutation,
  useListConfigQuery,
  useApplicationRecordQuery,
  useUpdateApplicationFormMutation,
  useSaveApplicationFormMutation,
  useGetApplicationMutation,
  useGetFormConfigMutation,
  useLazyGetDocumentsQuery,
  useUploadDocumentMutation,
  useDeleteDocumentMutation,
  useUpdateDocumentMutation,
  useDeleteApplicationMutation,
  useRestoreApplicationMutation,
  useGetRejectedApplicationsQuery,
  useForfeitApplicationMutation,
  useGetPublicStatusQuery,
  useFeeMapQuery,
} = applicationsApi;
